/* System defined core library */
import { Component, HostListener, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

/* User defined library/components */
import { ExamFeatureService } from '../../../shared/services/exam-feature.service';
import { RoutingService } from '../../../shared/services/routing.service';
import { UtilityService } from '../../../shared/services/utility.service';
import { AppointmentService } from '../../shared/services/appointment/appointment.service';
import { MetricApiService } from '../../shared/services/metric-api.service';
import { OrchestrationApiService } from '../../shared/services/orchestration-api.service';
import { SchedulingApiService } from '../../shared/services/scheduling-api.service';
import { SelectedExamDetailService } from '../../shared/services/selected-exam-detail.service';

/* User defined models */
import { AppointmentDetail } from '../../../shared/models/scheduling/appointmentdetail.model';
import { BatteryConfig } from '../../../shared/models/scheduling/batteryConfig.model';
import { Booking } from '../../../shared/models/scheduling/booking.model';
import { Candidate } from '../../../shared/models/scheduling/candidate.model';
import { Client } from '../../../shared/models/scheduling/client.model';
import { Payment } from '../../../shared/models/scheduling/payment.model';
import { PersonDetail } from '../../../shared/models/scheduling/personalDetail.model';
import { Program } from '../../../shared/models/scheduling/program.model';
import { Session } from '../../../shared/models/scheduling/session.model';
import { AppointmentSlot } from '../../../shared/models/scheduling/appointmentSlot.model';

/* User defined enums/constants */
import { ComponentType, Fee, LogLevel, NavigationAction } from '../../../shared/enums/scheduling.enum';


@Component({
  selector: 'app-cancellation-verification',
  templateUrl: './cancellation-verification.component.html'
})
export class CancellationVerificationComponent implements OnInit {
  @HostListener('window:popstate', ['$event'])

  onPopState(event: any) {
    this.selectedExamDetailService.cancellationFee = {
      totalAmount: 0,
      currency: null
    };

    this.metricApiService.logNavigation('Verification', NavigationAction.backwardsNavigation);
  }

  /* User defined private/public variables  */
  public booking: Booking;
  public showPostingInProgress: Boolean = false;
  public postedConfirmationNumber: string;
  public selectedAppointments: AppointmentDetail[] = [];
  public validCancelFlag: boolean = true;
  public postedErrorMessage: string;
  public totalExamDurations: number = 0;
  public candidate: Candidate;
  public personDetail: PersonDetail;
  public totalAmount: number = 0;
  public refundAmount: number = 0;
  public currencyCode: string = 'USD';
  public refundCurrency: string = 'USD';
  public isPaymentRequired: boolean = true;
  public errorMessage: string;
  public isPaytmentFailed: Boolean;
  public selectedClient: Client;
  public selectedProgram: Program;
  public cancelledConfirmationList: string[] = [];
  public isCancelPaymentTrue: boolean = true;
  public isCancelRefundTrue: boolean = false;
  public terminationUrl: string = "";
  public skipConfirmPage: boolean = false;
  public voucherPayment: Payment[];
  public batteryConfig: BatteryConfig;
  public cancelNotAllowed = false;


  /* Service subscriptions */
  booking$: Subscription;
  candidateData$: Subscription;
  client$: Subscription;
  program$: Subscription;
  translateLanguage$: Subscription;
  selectedAppointments$: Subscription;
  voucherPayment$: Subscription;
  batteryConfig$: Subscription;
  cancelNotAllowed$: Subscription;

  /* Constructor declaration with required injected services */
  constructor(
    private appointmentService: AppointmentService,
    private metricApiService: MetricApiService,
    private orchestrationApiService: OrchestrationApiService,
    private routingService: RoutingService,
    private schedulingApiService: SchedulingApiService,
    private selectedExamDetailService: SelectedExamDetailService,
    private translate: TranslateService,
    private utilityService: UtilityService
  ) {
    this.selectedAppointments$ = selectedExamDetailService.selectedAppointmentDetails.subscribe(
      selectedAppointments => {
        this.selectedAppointments = selectedAppointments;
      });

    this.candidateData$ = selectedExamDetailService.candidate.subscribe(
      candidate => {
        this.candidate = candidate;
        if (this.candidate) {
          this.personDetail = new PersonDetail(
            this.candidate.FirstName,
            this.candidate.LastName,
            this.candidate.AddressLine1,
            this.candidate.AddressLine2,
            this.candidate.AddressLine3,
            this.candidate.PrimaryPhoneNumber,
            this.candidate.EmailAddress
          );
        }
      });

    this.booking$ = selectedExamDetailService.booking.subscribe(
      booking => {
        this.booking = booking;
      });

    this.client$ = selectedExamDetailService.client.subscribe(
      client => {
        this.selectedClient = client;
      });

    this.program$ = selectedExamDetailService.program.subscribe(
      program => {
        this.selectedProgram = program;
      });

    translate.setDefaultLang(this.selectedExamDetailService.defaultLanguageCode);
    this.translateLanguage$ = selectedExamDetailService.languageCode.subscribe(
      languageCode => {
        translate.use(languageCode);
      });

    this.refundAmount = this.selectedExamDetailService.cancelRefund;
    this.refundCurrency = this.selectedExamDetailService.cancelRefundCurrency;

    this.voucherPayment$ = selectedExamDetailService.voucherPayment.subscribe(
      payment => {
        this.voucherPayment = payment;
      });

    this.batteryConfig$ = selectedExamDetailService.batteryConfig.subscribe(
      batteryConfig => {
        this.batteryConfig = batteryConfig;
      });

    this.cancelNotAllowed$ = selectedExamDetailService.cancelNotAllowed.subscribe(
      cancelNotAllowed => {
            this.cancelNotAllowed = cancelNotAllowed;
    });
  }

  /* Initialize the component & call require methods when component loaded first time. */
  ngOnInit() {
    sessionStorage.setItem('previousUrl', window.location.pathname);
    if (!this.utilityService.isDataValidToLoadComponent(ComponentType.CancellationVerificationComponent)) return;

    this.showPostingInProgress = false;
    this.postedConfirmationNumber = this.selectedExamDetailService.postedConfirmationNumber;
    this.isPaytmentFailed = (this.postedConfirmationNumber != "") ? true : false;

    this.terminationUrl = this.selectedExamDetailService.terminationUrl;
    this.skipConfirmPage = this.selectedExamDetailService.skipConfirmPage;

    if (this.selectedAppointments != null && this.selectedAppointments != undefined) {
      this.totalExamDurations = ExamFeatureService.calculateTotalDuration(this.selectedAppointments);
      this.validCancelFlag = false;

      if (!this.selectedExamDetailService.isBackToBackRequired) {
        this.getSingleExamPaymentDetails();
      } else {
        this.getExamSetPaymentDetails();
      }
      this.errorMessage = (!this.validCancelFlag) ? 'Appointment is not applicable for cancellation.' : '';
    }
  }

  /* use to go back in previous screen */
  goBack(): void {
    this.routingService.backwardNavigation(ComponentType.CancellationVerificationComponent);
    this.selectedExamDetailService.cancellationFee = {
      totalAmount: 0,
      currency: null
    };
  }

  /* check next button disable or not   */
  isDisable(): boolean {
    return !this.validCancelFlag || !!this.postedErrorMessage;
  }

  // get payment details on exams when not b2b
  getSingleExamPaymentDetails() {
    this.selectedAppointments.forEach(appointmentDetail => {
      let refund = appointmentDetail.payments.find((payment) => {
        return (payment.transactionType === Fee.Refund);
      });

      appointmentDetail.payments = [];
      this.schedulingApiService.getBookingPaymentFeeDetail(appointmentDetail.id, appointmentDetail.exam.id)
        .subscribe(paymentFee => {

          let scheduleFee = paymentFee.find((payment) => { return payment.type === Fee.InitialScheduleFee });

          paymentFee.forEach(payment => {
            if (payment.type == Fee.CancelFee) {
              let tmpPayment: Payment = new Payment(
                payment.amount, payment.currency || "USD", Fee.CancelFee
              );
              this.totalAmount += payment.amount;
              this.currencyCode = payment.currency || 'USD';
              appointmentDetail.payments.push(tmpPayment);
              this.validCancelFlag = true;

              if (!this.isCancelPaymentTrue) {
                this.totalAmount = 0;
                tmpPayment.amount = 0;
              }
              this.isPaymentRequired =
                this.isCancelPaymentTrue && this.totalAmount > 0;
              this.selectedExamDetailService.isPaymentRequired = this.isPaymentRequired;
              this.selectedExamDetailService.cancellationFee = {
                totalAmount: this.totalAmount,
                currency: payment.currency
              }
            } else if (payment.type === Fee.Refund) {
              if (!!refund) {
                if (!!this.selectedExamDetailService.cancelRefundQrTransaction) {
                  refund.refund = scheduleFee.amount;
                }
                appointmentDetail.payments.push(refund);
              }
            }
          });
        });

      /* Use to get Payment setting */
      this.orchestrationApiService.getFlows(this.selectedClient.code, this.selectedProgram.code, appointmentDetail.exam.code).subscribe(response => {
        this.selectedExamDetailService.setOrchestrationData(response);
        response = response.filter(function (el: any) {
          return el.name == "Payment";
        });

        // Set is payment required true when total amount and response of reschedule attribute value is true
        this.isCancelPaymentTrue = response.length && response[0].settings.cancel === 'True';
        this.isCancelRefundTrue = response.length && response[0].settings.cancelRefund === 'True';
        if (!this.isCancelPaymentTrue) {
          this.totalAmount = 0;
          this.selectedAppointments[0].payments.forEach((payment) => {
            if (payment.transactionType === 'Cancellation Fee') {
              payment.amount = 0;
            }
          })
        }

        if (this.isCancelPaymentTrue && this.isCancelRefundTrue) {
          this.metricApiService.log(LogLevel.Info, `Both Cancel Fee and Refund are configured for exam: ${this.selectedClient.code}/${this.selectedProgram.code}/${appointmentDetail.exam.code}`);
        }

        this.isPaymentRequired = this.isCancelPaymentTrue && this.totalAmount > 0;
        this.selectedExamDetailService.isPaymentRequired = this.isPaymentRequired;
        this.selectedExamDetailService.isRefundRequired = this.isCancelRefundTrue;
      });
    });
  }


  getApptDetail(appointmentDetail: AppointmentDetail, sessionIndex: number) {
    if (appointmentDetail.exam.sessions && appointmentDetail.exam.sessions.length < 1) return appointmentDetail;
    const session = appointmentDetail.exam.sessions[sessionIndex];
    let appointmentSlot =
      new AppointmentSlot(null, null, new Date(this.utilityService.getFormatToFullMMDDYYYY(session.slot.startDateTime, "/")), session.duration, session.site, session.slot.testTime, session.slot.testEndTime);
    appointmentDetail.appointmentSlot = appointmentSlot;
    return appointmentDetail;
  }

  /* getconfiguration details on all exams on an examset */
  getExamSetPaymentDetails() {
    this.orchestrationApiService.getExamSetFlows(
      this.selectedClient.code,
      this.selectedProgram.code,
      this.selectedExamDetailService.examSetId
    ).subscribe((response) => {

      this.selectedAppointments.forEach(appointmentDetail => {
        let refund = appointmentDetail.payments.find((payment) => {
          return (payment.transactionType === Fee.Refund);
        });

        appointmentDetail.payments = [];
        this.schedulingApiService.getBookingPaymentFeeDetail(appointmentDetail.id, appointmentDetail.exam.id)
          .subscribe(paymentFee => {

            let scheduleFee = paymentFee.find((payment) => { return payment.type === Fee.InitialScheduleFee });

            paymentFee.forEach(payment => {
              if (payment.type === Fee.CancelFee) {
                let tmpPayment: Payment = new Payment(
                  payment.amount,
                  payment.currency, Fee.CancelFee
                );

                this.currencyCode = payment.currency;
                appointmentDetail.payments.push(tmpPayment);
                this.validCancelFlag = true;
              } else if (payment.type === Fee.Refund) {
                if (!!refund) {
                  if (!!this.selectedExamDetailService.cancelRefundQrTransaction) {
                    refund.refund = scheduleFee.amount;
                  }
                  appointmentDetail.payments.push(refund);
                }
              }
            });
            let examData = response.filter((result: any) => {
              return appointmentDetail.exam.id === result.id;
            });
            if (examData.length) {
              let paymentData = examData[0].flowSet.filter((data: any) => {
                return data.name === "Payment";
              });

              appointmentDetail.exam.hasPayment = paymentData[0].settings.cancel === 'True';
              this.selectedExamDetailService.isRefundRequired = paymentData.some((data: any) => data.settings.cancelRefund === 'True');

              if (appointmentDetail.exam.hasPayment && this.selectedExamDetailService.isRefundRequired) {
                this.metricApiService.log(LogLevel.Info, `Both Cancel Fee and Refund are configured for exam: ${this.selectedClient.code}/${this.selectedProgram.code}/${appointmentDetail.exam.code}`);
              }

              if (!appointmentDetail.exam.hasPayment) {
                appointmentDetail.payments.forEach((payment) => {
                  if (payment.transactionType === Fee.CancelFee) {
                    payment.amount = 0;
                  }
                });
              }
            }
            // Note: Fee or Refund may be payments[0], depending on order returned from API
            appointmentDetail.payments.forEach(payment => {
              this.totalAmount += payment.transactionType === Fee.CancelFee ? payment.amount : 0;
            });
            this.isPaymentRequired = this.selectedAppointments.some(appt => appt.exam.hasPayment) && this.totalAmount > 0;
            this.selectedExamDetailService.setSelectedAppointmentDetails(this.selectedAppointments);
            this.selectedExamDetailService.isPaymentRequired = this.isPaymentRequired;
            this.selectedExamDetailService.cancellationFee = {
              totalAmount: this.totalAmount,
              currency: this.currencyCode
            };
          });
      });
    });
  }

  /* Use to perform cancellation */
  submit(postData: any): void {
    if (!this.isPaymentRequired) {

      this.selectedExamDetailService.isPaymentConfirmed = true;
      this.selectedExamDetailService.postedConfirmationNumber = "";

      let confirmationNumbers: string[] = [];
      let appointmentIds: string[] = [];
      let examDetailsForSettlement: any[] = [];
      let examAccommodationsForNativeDelete: any[] = [];


      this.selectedAppointments.forEach((appointment: AppointmentDetail) => {
        examAccommodationsForNativeDelete.push({
          'id': appointment.exam.id,
          'testingAccommodations': appointment.exam.testingAccommodations
        });
            if (appointment.exam.sessions && appointment.exam.sessions.length) {
              appointment.exam.sessions.forEach((session: Session) => {
                confirmationNumbers.push(session.confirmationNumber);
                appointmentIds.push(session.appointmentId);
                examDetailsForSettlement.push(
                  {
                    "confirmationNumber": session.appointmentId,
                    "fee": 0
                  }
                );
              })
            } else {
              confirmationNumbers.push(appointment.ConfirmationNumber);
              appointmentIds.push(appointment.id);
              examDetailsForSettlement.push(
                {
                  "confirmationNumber": appointment.id,
                  "fee": 0
                });
            }
          });

      let refundDetails = {
        'amount': this.selectedExamDetailService.cancelRefund,
        'currency': this.selectedExamDetailService.cancelRefundCurrency,
        'system': 'USR',
        'transactionId': this.selectedExamDetailService.scheduleTransactionId,
        'paymentType': this.selectedExamDetailService.cancelRefundPaymentType,
        'transaction': this.selectedExamDetailService.cancelRefundQrTransaction
      };

      this.schedulingApiService.cancelBookingWithoutPayment(
        appointmentIds,
        examAccommodationsForNativeDelete,
        refundDetails,
        this.voucherPayment,
        this.selectedAppointments[0].ConfirmationNumber
      ).subscribe(cancelResp => {
        if (cancelResp.status) {
          this.selectedExamDetailService.postedConfirmationNumber = '';
          this.cancelledConfirmationList = [];
          cancelResp.response.tests.forEach((test: any) => {
            if (this.selectedAppointments[0].exam.sessions && this.selectedAppointments[0].exam.sessions.length) {
              this.selectedExamDetailService.postedConfirmationNumber = this.selectedExamDetailService.postedConfirmationNumber
                .concat(test.sessions[0].confirmationNumber + "(" + this.selectedAppointments[0].exam.name + ") <br />");
            } else {
              let selectedAppointment: any = this.selectedAppointments
                .filter((selectedAppointment: any) => {
                  return selectedAppointment.ConfirmationNumber === test.sessions[0].confirmationNumber;
                });

              this.selectedExamDetailService.postedConfirmationNumber = this.selectedExamDetailService.postedConfirmationNumber
                .concat(test.sessions[0].confirmationNumber + "(" + selectedAppointment[0].exam.name + ") <br />");
            }

            this.cancelledConfirmationList.push(test.sessions[0].confirmationNumber);
          });
          this.selectedExamDetailService.setPostedConfirmationList(this.cancelledConfirmationList);

          // Remove </br> tag end of the string
          this.selectedExamDetailService.postedConfirmationNumber = this.selectedExamDetailService.postedConfirmationNumber
              .substring(0, this.selectedExamDetailService.postedConfirmationNumber.length - 6);

          this.selectedExamDetailService.isPaymentConfirmed = true;
          this.selectedExamDetailService.postedErrorMessage = "";

          this.routingService.cancelFwdNavigation(ComponentType.CancellationVerificationComponent);
        }
        else {
          this.selectedExamDetailService.isPaymentConfirmed = false;
          this.selectedExamDetailService.postedConfirmationNumber = "Error";
          this.postedErrorMessage = cancelResp.message;
        }
      });
    } else {
      this.routingService.cancelFwdNavigation(ComponentType.CancellationVerificationComponent);
    }
  }

  public canHideButton(): boolean {
    return (this.selectedExamDetailService.setCancelPathLock && this.selectedExamDetailService.skipAppointmentLookup);
  }

  //check if total duration can be displayed, based on the type of exam
  canShowDuration(): boolean {
    return this.appointmentService.canShowDuration(this.selectedAppointments);
  }
}
